import {useAuth} from '@/modules/auth/api'
import {useGetSettings} from '@/modules/settings/api'
import {isBlockedByPastTherapyDate} from '@cypro/engine/utils'
import {usePastTherapyDateWarning} from '@/modules/patients-therapies/components/past-therapy-date-alert'
import {Action, useTherapySequenceActions} from '@/modules/patients-therapies/hooks'
import {useFormatTherapySequenceLabel} from '@/modules/patients-therapies/hooks/use-format-therapy-sequence-label'
import {SequenceActionButton} from '@/modules/patients-therapies/components/sequence-action-button'
import {useGetUser} from '@/modules/users/api'
import {Box, ButtonProps, Text} from '@chakra-ui/react'
import {PatientTherapySequenceStatus} from '@cypro/engine/database/schema'
import {PatientTherapySequenceEvent, PatientTherapySequenceMachine} from '@cypro/engine/mutators'
import {PatientTherapySequence} from '@cypro/engine/schema'
import {useAppForm} from '@cypro/ui/integrations/form'
import {useTranslate} from '@tolgee/react'
import {LuChevronDown} from 'react-icons/lu'
interface Props {
  sequence: PatientTherapySequence
  buttonProps?: ButtonProps
}

export function TherapySequenceActions(props: Props) {
  const {auth} = useAuth()
  const [user] = useGetUser(auth?.user?.id || '')
  const {settings} = useGetSettings()

  return (
    <TherapySequenceActionsContent
      key={PatientTherapySequenceMachine.buildKey({user, sequence: props.sequence, settings})}
      {...props}
    />
  )
}

function TherapySequenceActionsContent({sequence, buttonProps}: Props) {
  const {t} = useTranslate('activity')
  const {t: tCommon} = useTranslate('common')
  const {actions, statusLabels, sendEvent} = useTherapySequenceActions(sequence)
  const formatLabel = useFormatTherapySequenceLabel(sequence)

  const form = useAppForm({
    defaultValues: {action: null} as {
      action: Action | null
    },
    listeners: {
      async onChange({formApi}) {
        const {action} = formApi.state.values
        if (action) await sendEvent({id: sequence.id, event: {type: action} as PatientTherapySequenceEvent})
        formApi.reset()
      },
    },
  })

  const isSequenceInThePast = isBlockedByPastTherapyDate(sequence)
  const pastDateWarning = usePastTherapyDateWarning(sequence)

  const btnProps = {size: '2xs', variant: 'surface', colorPalette: 'gray', ...buttonProps} as const
  const tooltipProps = {
    content: isSequenceInThePast ? tCommon('therapy_sequence_disabled_because_is_passed') : pastDateWarning,
  } as const

  if (sequence.isNote)
    return (
      <SequenceActionButton
        as="div"
        {...btnProps}
        sequence={sequence}
        disabledReason={tooltipProps.content}
        disabled={btnProps?.disabled || isSequenceInThePast}
      >
        {sequence.status === PatientTherapySequenceStatus.enum.pending_doctor_validation ? t('check') : t('checked')}
      </SequenceActionButton>
    )

  if (!actions.length && !statusLabels.length)
    return (
      <Text fontSize="sm" color="fg.muted">
        {t('no_actions_available')}
      </Text>
    )

  if (!actions.length)
    return (
      <Box>
        {statusLabels.map(label => (
          <Text key={label.status} fontSize="sm" color="fg.muted">
            {label.title}
          </Text>
        ))}
      </Box>
    )

  const [firstAction] = actions

  if (actions.length === 1) {
    return (
      <SequenceActionButton
        key={`${sequence.id}.${firstAction}`}
        {...btnProps}
        sequence={sequence}
        action={firstAction}
        disabledReason={tooltipProps.content}
        disabled={btnProps?.disabled || isSequenceInThePast}
        onClick={async event => {
          event.stopPropagation()
          await sendEvent({id: sequence.id, event: {type: firstAction} as PatientTherapySequenceEvent})
        }}
      >
        {formatLabel(firstAction, 'action')}
      </SequenceActionButton>
    )
  }

  return (
    <form onSubmit={e => e.preventDefault()} onClick={e => e.stopPropagation()}>
      <form.AppForm>
        <form.AppField name="action" mode="value">
          {field => (
            <field.Select
              items={actions.map(a => ({label: formatLabel(a, 'action'), value: a}))}
              label={t('actions')}
              hideLabel
              positioning={{placement: 'bottom-start'}}
              disabled={isSequenceInThePast}
            >
              <SequenceActionButton
                {...btnProps}
                width="100%"
                sequence={sequence}
                action={firstAction}
                disabledReason={tooltipProps.content}
              >
                {formatLabel(firstAction, 'action')}
                <LuChevronDown />
              </SequenceActionButton>
            </field.Select>
          )}
        </form.AppField>
      </form.AppForm>
    </form>
  )
}
